/**
 * Pure logic for the workspace switcher's progressive-disclosure search.
 *
 * Kept as framework-free functions (no Angular, no DOM) so they can be unit
 * tested directly under bare vitest with full branch coverage — mirroring the
 * repo's "pure logic in .ts, co-located pure spec" convention.
 *
 * SHARED, and that is the whole reason it is a file rather than three
 * methods. The switcher moved out of the shell bar and into the profile
 * panel's "Switch workspace" row (`41-profile-panel`), and a move that
 * reimplements the threshold and the match rule at the destination is a
 * rewrite wearing a move's name — two lists that answer "does this
 * organisation match?" differently is the defect, not the duplication.
 */

/** Minimal shape the filter needs — anything with a display `name`. */
export interface NamedCustomer {
  name: string;
}

/**
 * Above this many accessible organisations the switcher swaps its plain flat
 * list for a name-filter search box (progressive disclosure).
 *
 * It lives HERE, beside the predicate that reads it, so the surface that
 * renders the list and the function that decides whether to offer a search
 * box cannot drift apart. `org-switcher.component.ts` re-exports it for its
 * own spec.
 */
export const SWITCHER_SEARCH_THRESHOLD = 5;

/**
 * Whether the switcher should render its name-filter search box. The plain flat
 * list stays until the accessible-customer count exceeds `threshold`.
 */
export function shouldShowSearch(count: number, threshold: number): boolean {
  return count > threshold;
}

/**
 * Filter `list` by a case-insensitive substring match on each customer's name.
 * An empty / whitespace-only query returns the full list unchanged.
 */
export function filterCustomers<T extends NamedCustomer>(
  list: readonly T[],
  query: string,
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return list.slice();
  return list.filter((c) => c.name.toLowerCase().includes(q));
}
